import React, { useEffect, useRef, useState } from "react";
import { HOTMART_URL } from "../../lib/constants";
import { ArrowRight } from "lucide-react";

const CINEMATIC_VIDEO =
  "https://customer-assets.emergentagent.com/job_tower-crane-pro/artifacts/k4qgaed8_0a8d068216f045bbaa32d9875c68499e.MOV";
const POSTER =
  "https://images.pexels.com/photos/1787044/pexels-photo-1787044.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";

export default function CinematicView() {
  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    const section = sectionRef.current;
    if (!video || !section) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            // Fade in + play when visible
            setLoaded(true);
            const p = video.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(section);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-[85vh] flex items-center overflow-hidden border-b border-white/10 bg-black"
      data-testid="cinematic-view"
    >
      {/* Video background */}
      <video
        ref={videoRef}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
          loaded ? "opacity-100" : "opacity-0"
        }`}
        src={CINEMATIC_VIDEO}
        poster={POSTER}
        muted
        loop
        playsInline
        preload="metadata"
        aria-hidden="true"
        data-testid="cinematic-video"
      />
      {/* Fallback poster while video loads */}
      <div
        aria-hidden="true"
        className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${
          loaded ? "opacity-0" : "opacity-100"
        }`}
        style={{ backgroundImage: `url(${POSTER})` }}
      />

      {/* Subtle 30% gradient for text legibility on left */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent"
      />
      <div aria-hidden="true" className="absolute inset-0 grain" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-yellow-400 mb-4">
            <span className="w-2 h-2 bg-yellow-400" /> +5 anos no comando
          </div>
          <h2 className="font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.85]">
            ESTA PODE SER
            <br />
            <span className="text-yellow-400">A TUA VISTA.</span>
          </h2>
          <p className="mt-6 text-zinc-300 text-xl leading-relaxed">
            Todos os dias. <span className="text-white font-semibold">De cima do mundo.</span>
          </p>

          <a
            href={HOTMART_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cinematic-cta-button"
            className="mt-10 group inline-flex items-center gap-3 border-b-2 border-yellow-400 text-yellow-400 font-display uppercase tracking-widest text-xl pb-2 hover:gap-5 transition-all"
          >
            Quero chegar aqui
            <ArrowRight className="w-6 h-6 transition-transform group-hover:translate-x-1" />
          </a>
        </div>
      </div>
    </section>
  );
}
